import DateFnsUtils from "@date-io/date-fns";
import React from "react";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { create } from "jss";
import {
  StylesProvider,
  createGenerateClassName,
  jssPreset
} from "@material-ui/styles";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import withStyles from "@material-ui/core/styles/withStyles";
import AuthProvider from "../auth/AuthProvider";
import Content from "./Content";
import Design from "../design/Design";
import DesignProvider from "../design/DesignProvider";
import DocumentTitle from "./DocumentTitle";
import ErrorBoundary from "./ErrorBoundary";
import Sheet from "./Sheet";
import Themer from "./Themer";
import WorkflowProvider from "../workflow/WorkflowProvider";

const generateClassName = createGenerateClassName({ productionPrefix: "_" });
const jss = create(jssPreset());
const helmetContext = {};
const NullProvider = ({ children = null }) => children;

const AppFallbackComponent = ({ error }) => (
  <div>
    <p>
      <strong>Oops! An error occured!</strong>
    </p>
    <p>
      <strong>Error:</strong> {error.toString()}
    </p>
  </div>
);

const InnerApp = withStyles({}, { name: "LcApp" })(
  ({
    nav,
    menu,
    content = Content,
    footer,
    login,
    routes,
    disableReCaptcha,
    reCaptchaSiteKey = window.reCaptchaSiteKey,
    ReCaptchaProvider = disableReCaptcha
      ? NullProvider
      : GoogleReCaptchaProvider,
    CustomProvider = NullProvider,
    FallbackComponent = AppFallbackComponent,
    onError,
    app
  }) => (
    <ErrorBoundary onError={onError} FallbackComponent={FallbackComponent}>
      <DesignProvider>
        <HelmetProvider context={helmetContext}>
          <ReCaptchaProvider reCaptchaKey={reCaptchaSiteKey}>
            <AuthProvider disableReCaptcha={disableReCaptcha}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <CustomProvider>
                  <DocumentTitle />
                  <WorkflowProvider {...app}>
                    <Sheet
                      nav={nav}
                      menu={menu}
                      content={content}
                      login={login}
                      routes={routes}
                      footer={footer}
                    />
                    <Design />
                  </WorkflowProvider>
                </CustomProvider>
              </MuiPickersUtilsProvider>
            </AuthProvider>
          </ReCaptchaProvider>
        </HelmetProvider>
      </DesignProvider>
    </ErrorBoundary>
  )
);

const App = ({ theme, pathPrefix, ...rest }) => (
  <BrowserRouter basename={pathPrefix}>
    <StylesProvider jss={jss} generateClassName={generateClassName}>
      <Themer theme={theme}>
        <InnerApp {...rest} />
      </Themer>
    </StylesProvider>
  </BrowserRouter>
);

export default App;
